<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto bg-taupe-50"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-6 sm:pt-8 lg:pt-10 xl:max-w-7xl xl:mx-auto w-full"
        >
            <h1 id="primary-heading" class="sr-only">Admin: Account</h1>
            <div class="px-4 sm:px-6 lg:px-8 pb-12 space-y-8">
                <div class="sm:flex sm:items-center sm:justify-between">
                    <div class="sm:flex-auto">
                        <admin-header-tabs current="account" />
                    </div>
                </div>

                <form @submit.prevent="saveProfile" class="space-y-8">
                    <div
                        class="rounded-xl bg-white shadow-sm border border-taupe-200 p-6 space-y-6 hover:shadow-md transition-shadow"
                    >
                        <div class="space-y-1">
                            <h2 class="text-2xl font-semibold text-taupe-900">
                                Account Settings
                            </h2>
                            <p class="text-sm text-taupe-600">
                                Manage your account name.
                            </p>
                        </div>

                        <div class="border-t border-taupe-200 pt-6 space-y-6">
                            <div class="space-y-2">
                                <label
                                    for="name"
                                    class="block text-sm font-medium text-taupe-900"
                                    >Account Name</label
                                >
                                <p class="text-sm text-taupe-600">
                                    The name of your organization or account.
                                </p>
                                <input
                                    type="text"
                                    v-model="data.name"
                                    name="name"
                                    id="name"
                                    autocomplete="organization-name"
                                    placeholder="Your account name"
                                    class="block w-full max-w-xl px-4 py-2.5 rounded-lg border border-taupe-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 transition-colors focus:outline-hidden focus:ring-2 focus:ring-offset-2"
                                />
                            </div>
                        </div>
                    </div>

                    <div
                        class="rounded-xl bg-white shadow-sm border border-taupe-200 p-6 space-y-6 hover:shadow-md transition-shadow"
                    >
                        <div class="space-y-1">
                            <h2 class="text-2xl font-semibold text-taupe-900">
                                Preferences
                            </h2>
                            <p class="text-sm text-taupe-600">
                                Configure timezone and date/time display
                                preferences for your account.
                            </p>
                        </div>

                        <div class="border-t border-taupe-200 pt-6 space-y-6">
                            <div class="space-y-2">
                                <label
                                    for="timezone"
                                    class="block text-sm font-medium text-taupe-900"
                                    >Timezone</label
                                >
                                <p class="text-sm text-taupe-600">
                                    Your timezone is used for all reporting and
                                    scheduling in Zenmanage.
                                </p>
                                <select
                                    v-model="data.timezone"
                                    name="timezone"
                                    id="timezone"
                                    class="block w-full max-w-xl px-4 py-2.5 rounded-lg border border-taupe-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 transition-colors focus:outline-hidden focus:ring-2 focus:ring-offset-2"
                                >
                                    <option disabled value="">
                                        Select a timezone
                                    </option>
                                    <option
                                        v-for="c in countries"
                                        :key="c"
                                        :value="c"
                                    >
                                        {{ c }}
                                    </option>
                                </select>
                            </div>

                            <div class="space-y-2">
                                <label
                                    for="date-format"
                                    class="block text-sm font-medium text-taupe-900"
                                    >Date Format</label
                                >
                                <p class="text-sm text-taupe-600">
                                    Choose how dates are displayed throughout
                                    the application for all users on this
                                    account (unless overridden by user
                                    preferences).
                                </p>
                                <select
                                    v-model="data.date_format"
                                    name="date-format"
                                    id="date-format"
                                    class="block w-full max-w-xl px-4 py-2.5 rounded-lg border border-taupe-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 transition-colors focus:outline-hidden focus:ring-2 focus:ring-offset-2"
                                >
                                    <option :value="null">
                                        Default (MMM D, YYYY)
                                    </option>
                                    <option
                                        v-for="df in dateFormats"
                                        :key="df.value"
                                        :value="df.value"
                                    >
                                        {{
                                            moment(Date.now()).format(df.value)
                                        }}
                                    </option>
                                </select>
                            </div>

                            <div class="space-y-2">
                                <label
                                    for="time-format"
                                    class="block text-sm font-medium text-taupe-900"
                                    >Time Format</label
                                >
                                <p class="text-sm text-taupe-600">
                                    Choose how times are displayed throughout
                                    the application for all users on this
                                    account (unless overridden by user
                                    preferences).
                                </p>
                                <select
                                    v-model="data.time_format"
                                    name="time-format"
                                    id="time-format"
                                    class="block w-full max-w-xl px-4 py-2.5 rounded-lg border border-taupe-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 transition-colors focus:outline-hidden focus:ring-2 focus:ring-offset-2"
                                >
                                    <option :value="null">
                                        Default (h:mm a)
                                    </option>
                                    <option
                                        v-for="tf in timeFormats"
                                        :key="tf.value"
                                        :value="tf.value"
                                    >
                                        {{
                                            moment(Date.now()).format(tf.value)
                                        }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div class="flex gap-3 pt-4">
                        <zen-button
                            v-if="accountChanged"
                            type="button"
                            variant="secondary"
                            @click="reset"
                        >
                            Cancel
                        </zen-button>
                        <zen-button
                            v-if="can(PERMISSIONS.ACCOUNT_UPDATE)"
                            type="submit"
                            variant="primary"
                            :disabled="!accountChanged"
                            :processing="saving"
                        >
                            Save Changes
                        </zen-button>
                    </div>
                </form>

                <div class="w-full space-y-6 pt-8 border-t border-taupe-200">
                    <div class="space-y-1">
                        <h2 class="text-2xl font-semibold text-red-600">
                            Danger Zone
                        </h2>
                        <p class="text-sm text-taupe-600">
                            Irreversible and destructive actions.
                        </p>
                    </div>

                    <div
                        v-if="can(PERMISSIONS.ACCOUNT_DELETE)"
                        class="rounded-xl border-2 border-red-200 bg-red-50 p-6 max-w-md hover:shadow-lg transition-shadow"
                    >
                        <div class="flex gap-4">
                            <svg
                                class="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M8 7h12m0 0l-4-4m4 4l-4 4M16 17H4m0 0l4 4m-4-4l4-4"
                                />
                            </svg>
                            <div class="flex-1">
                                <div class="font-medium text-red-900 mb-1">
                                    Transfer Ownership
                                </div>
                                <p class="text-sm text-red-800 mb-4">
                                    Hand off account ownership to another admin.
                                    You will be downgraded to the Admin role and
                                    will no longer be able to delete the account
                                    or transfer ownership yourself.
                                </p>

                                <p
                                    v-if="loadingEligibleAdmins"
                                    class="text-sm text-red-800 mb-4"
                                    data-testid="transfer-ownership-loading"
                                >
                                    Loading eligible admins&hellip;
                                </p>
                                <p
                                    v-else-if="eligibleAdmins.length === 0"
                                    class="text-sm text-red-800 mb-4"
                                    data-testid="transfer-ownership-empty"
                                >
                                    No other admins exist yet. Grant a user the
                                    Admin role before transferring ownership to
                                    them.
                                </p>
                                <template v-else>
                                    <select
                                        v-model="selectedNewOwnerUlid"
                                        aria-label="New owner"
                                        class="block w-full px-4 py-2.5 rounded-lg border border-red-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 transition-colors focus:outline-hidden focus:ring-2 focus:ring-offset-2 mb-4 bg-white"
                                    >
                                        <option value="" disabled>
                                            Select a new owner
                                        </option>
                                        <option
                                            v-for="admin in eligibleAdmins"
                                            :key="admin.ulid"
                                            :value="admin.ulid"
                                        >
                                            {{ adminLabel(admin) }}
                                        </option>
                                    </select>
                                    <zen-button
                                        @click="toggleTransferModal()"
                                        variant="primary-danger"
                                        :disabled="!selectedNewOwnerUlid"
                                    >
                                        Transfer Ownership
                                    </zen-button>
                                </template>
                            </div>
                        </div>
                    </div>

                    <div
                        v-if="can(PERMISSIONS.ACCOUNT_DELETE)"
                        class="rounded-xl border-2 border-red-200 bg-red-50 p-6 max-w-md hover:shadow-lg transition-shadow"
                    >
                        <div class="flex gap-4">
                            <svg
                                class="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                            </svg>
                            <div class="flex-1">
                                <div class="font-medium text-red-900 mb-1">
                                    Delete Account
                                </div>
                                <p class="text-sm text-red-800 mb-4">
                                    Permanently delete your account and all
                                    associated data. Your account will be
                                    deleted immediately. Any credit left on it
                                    is forfeited, and nothing is refunded. This
                                    action cannot be undone.
                                </p>
                                <zen-button
                                    @click="toggleDeleteModal()"
                                    variant="primary-danger"
                                >
                                    <template #icon>
                                        <svg
                                            class="h-4 w-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                            />
                                        </svg>
                                    </template>
                                    Delete Account
                                </zen-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <transfer-ownership-modal
            :is-open="modals.transfer.show"
            :processing="modals.transfer.processing"
            :new-owner-name="selectedNewOwnerName"
            @close="toggleTransferModal"
            @confirm="confirmTransferOwnership"
        />

        <delete-account-modal
            :is-open="modals.delete.show"
            :processing="modals.delete.processing"
            @close="toggleDeleteModal"
            @confirm="confirmDelete"
        />
    </main>
</template>

<script>
import moment from 'moment'
import AdminHeaderTabs from '@/screens/_components/admin/HeaderTabs.vue'
import DeleteAccountModal from '@/screens/trusted/admin/modals/DeleteAccountModal.vue'
import TransferOwnershipModal from '@/screens/trusted/admin/modals/TransferOwnershipModal.vue'
import { useSessionStore } from '@/stores/session'
import { useAuthStore } from '@/stores/auth'
import { useDateFormatter } from '@/screens/_system/DateTime.vue'

import countries from '@/helpers/countries'
import { unpaidBalanceMessage } from '@/helpers/delete-account'

import ProfileAccountService from '@/services/profile-account.service'
import UserService from '@/services/user.service'
import { errorMessage, successMessage } from '@/utils/notifications'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'

export default {
    name: 'AdminAccountScreen',
    setup() {
        const { can } = usePermissions()
        return { can, PERMISSIONS }
    },
    components: {
        AdminHeaderTabs,
        DeleteAccountModal,
        TransferOwnershipModal,
    },
    computed: {
        sessionStore() {
            return useSessionStore()
        },
        authStore() {
            return useAuthStore()
        },
        countries() {
            return countries()
        },
        accountChanged() {
            return (
                JSON.stringify(this.data) !==
                JSON.stringify(this.sessionStore.data.account.data)
            )
        },
        selectedNewOwnerName() {
            const admin = this.eligibleAdmins.find(
                (a) => a.ulid === this.selectedNewOwnerUlid
            )
            return admin ? this.adminLabel(admin) : ''
        },
    },
    data() {
        const { DATE_FORMATS, TIME_FORMATS } = useDateFormatter()
        return {
            saving: false,
            data: {},
            dateFormats: DATE_FORMATS,
            timeFormats: TIME_FORMATS,
            eligibleAdmins: [],
            loadingEligibleAdmins: false,
            selectedNewOwnerUlid: '',
            modals: {
                delete: {
                    show: false,
                    processing: false,
                },
                transfer: {
                    show: false,
                    processing: false,
                },
            },
        }
    },
    methods: {
        moment(datetime) {
            return moment(datetime)
        },
        adminLabel(admin) {
            const name =
                admin.display_name || `${admin.first_name} ${admin.last_name}`
            return `${name} (${admin.email})`
        },
        loadEligibleAdmins() {
            this.loadingEligibleAdmins = true
            const selfUlid = this.sessionStore.data.user.data.ulid

            return Promise.all([
                UserService.list(),
                UserService.listAllGrants(),
            ])
                .then(([usersRes, grantsRes]) => {
                    const candidates = (usersRes.data ?? []).filter(
                        (user) => user.ulid !== selfUlid
                    )

                    const adminUserUlids = new Set(
                        (grantsRes.data ?? [])
                            .filter(
                                (grant) =>
                                    grant.scope_type === 'account' &&
                                    grant.role === 'admin'
                            )
                            .map((grant) => grant.principal_ulid)
                    )

                    this.eligibleAdmins = candidates.filter((user) =>
                        adminUserUlids.has(user.ulid)
                    )
                })
                .catch((error) => {
                    errorMessage(error)
                })
                .finally(() => {
                    this.loadingEligibleAdmins = false
                })
        },
        toggleTransferModal() {
            this.modals.transfer.show = !this.modals.transfer.show
        },
        confirmTransferOwnership() {
            this.modals.transfer.processing = true
            ProfileAccountService.transferOwnership(this.selectedNewOwnerUlid)
                .then(() => {
                    successMessage('Account ownership has been transferred.')
                    this.sessionStore.setUserEffectiveRole('admin')
                    this.modals.transfer.show = false
                    this.selectedNewOwnerUlid = ''
                })
                .catch((error) => {
                    const fieldError =
                        error.response?.data?.errors?.new_owner_ulid?.[0]
                    errorMessage(fieldError || error)
                })
                .finally(() => {
                    this.modals.transfer.processing = false
                })
        },
        confirmDelete() {
            this.modals.delete.processing = true
            ProfileAccountService.remove()
                .then(() => {
                    return this.authStore.logout(true).then(() => {
                        return this.$router.push('/login')
                    })
                })
                .catch((error) => {
                    errorMessage(unpaidBalanceMessage(error) ?? error)
                })
                .finally(() => {
                    this.modals.delete.processing = false
                })
        },
        toggleDeleteModal() {
            this.modals.delete.show = !this.modals.delete.show
        },
        reset() {
            this.data = JSON.parse(
                JSON.stringify(this.sessionStore.data.account.data)
            )
        },
        saveProfile() {
            this.saving = true

            this.sessionStore
                .updateAccount(this.data)
                .then(() => {
                    this.reset()
                    successMessage('Your account has been updated.')
                })
                .catch((error) => {
                    errorMessage(error)
                })
                .finally(() => {
                    this.saving = false
                })
        },
    },
    mounted() {
        this.reset()

        if (this.can(PERMISSIONS.ACCOUNT_DELETE)) {
            this.loadEligibleAdmins()
        }
    },
}
</script>
